import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { migrateTripPlaces, type Trip } from '@my-fullstack-app/itinerary-types';
import { scopedEncryptedStorage } from '../lib/scoped-encrypted-storage';
import { defaultLayerPrefs } from '../lib/layer-registry';
import { defaultReviewLayerPrefs } from '../lib/review-layer-registry';
import { DEFAULT_HAPTICS_ENABLED } from './slices/view-slice';
import { createTripSlice } from './slices/trip-slice';
import { createDaySlice } from './slices/day-slice';
import { createPlaceSlice } from './slices/place-slice';
import { createTransportSlice } from './slices/transport-slice';
import { createCustomSlice } from './slices/custom-slice';
import { createJourneySlice } from './slices/journey-slice';
import { createStaySlice } from './slices/stay-slice';
import { createWishlistSlice } from './slices/wishlist-slice';
import { createParticipantSlice } from './slices/participant-slice';
import { createCostItemsSlice } from './slices/cost-items-slice';
import { createFxSlice } from './slices/fx-slice';
import { createNotesSlice } from './slices/notes-slice';
import { createHistorySlice } from './slices/history-slice';
import { createSyncSlice } from './slices/sync-slice';
import { createViewSlice } from './slices/view-slice';
import { createLayersSlice } from './slices/layers-slice';
import { createUserPrefsSlice } from './slices/user-prefs-slice';
import { createReferencesSlice } from './slices/references-slice';
import { createAttachmentsSlice } from './slices/attachments-slice';

export type { TripRole } from './slices/trip-slice';

import type { TripSlice } from './slices/trip-slice';
import type { DaySlice } from './slices/day-slice';
import type { PlaceSlice } from './slices/place-slice';
import type { TransportSlice } from './slices/transport-slice';
import type { CustomSlice } from './slices/custom-slice';
import type { JourneySlice } from './slices/journey-slice';
import type { StaySlice } from './slices/stay-slice';
import type { WishlistSlice } from './slices/wishlist-slice';
import type { ParticipantSlice } from './slices/participant-slice';
import type { CostItemsSlice } from './slices/cost-items-slice';
import type { FxSlice } from './slices/fx-slice';
import type { NotesSlice } from './slices/notes-slice';
import type { HistorySlice } from './slices/history-slice';
import type { SyncSlice } from './slices/sync-slice';
import type { ViewSlice } from './slices/view-slice';
import type { LayersSlice } from './slices/layers-slice';
import type { UserPrefsSlice } from './slices/user-prefs-slice';
import type { ReferencesSlice } from './slices/references-slice';
import type { AttachmentsSlice } from './slices/attachments-slice';

export type ItineraryState =
  & TripSlice
  & DaySlice
  & PlaceSlice
  & TransportSlice
  & CustomSlice
  & JourneySlice
  & StaySlice
  & WishlistSlice
  & ParticipantSlice
  & CostItemsSlice
  & FxSlice
  & NotesSlice
  & HistorySlice
  & SyncSlice
  & ViewSlice
  & LayersSlice
  & UserPrefsSlice
  & ReferencesSlice
  & AttachmentsSlice;

const STORAGE_KEY = 'itinerary-trips';

/** Module-level "persist rehydration finished" flag + listener registry.
 *  Exposed so the splash gate (and any non-React cold-start consumer) can
 *  block until the persisted store has merged into memory. Kept off the
 *  store state on purpose — splash-hide is a one-shot signal and we don't
 *  want every store selector to re-render the moment hydration completes. */
let _storeHydrated = false;
const _storeHydrationListeners: Array<() => void> = [];

export function getStoreHydrated(): boolean {
  return _storeHydrated;
}

/** Subscribe to "persist rehydration finished". Fires immediately if
 *  hydration already happened (covers late subscribers — Zustand's
 *  built-in `onFinishHydration` only catches the next finish event). */
export function subscribeStoreHydration(listener: () => void): () => void {
  const unsubscribe = () => {
    const i = _storeHydrationListeners.indexOf(listener);
    if (i >= 0) _storeHydrationListeners.splice(i, 1);
  };
  if (_storeHydrated) {
    listener();
    return unsubscribe;
  }
  _storeHydrationListeners.push(listener);
  return unsubscribe;
}

/**
 * Picks the keys that travel to AsyncStorage. Extracted as a named
 * function (rather than inlined in the persist config) so the
 * derived `Persisted` type below can enforce key + value-type parity
 * on `persistedDefaults()`. With Zustand's default
 * `{...current, ...persisted}` merge a key absent from the target
 * scope's storage would preserve the previous scope's in-memory
 * value — `persistedDefaults` resets it.
 *
 * Adding a new persisted key here ALSO requires adding it to
 * `persistedDefaults()` below; the typechecker will catch the
 * omission via `Required<Persisted>`.
 */
function partializeItineraryState(state: ItineraryState) {
  return {
    trips: state.trips,
    activeTripId: state.activeTripId,
    notesMap: state.notesMap,
    notesMetaMap: state.notesMetaMap,
    // Persist so tags survive cold starts for both anonymous and
    // logged-in users. Server hydration wins on login via merge in
    // hydrateFromServer (server tags are authoritative for synced trips).
    tagsMap: state.tagsMap,
    // Persisted so a closed tab doesn't drop in-flight mutations —
    // the next launch resumes with the same dirty queue.
    dirtyTrips: (state as unknown as { dirtyTrips: Record<string, unknown> }).dirtyTrips,
    pendingDeletes: (state as unknown as { pendingDeletes: string[] }).pendingDeletes,
    // UI layer prefs — sparse since ADR-0082. The effective values
    // map (used by `useLayer(id)` reads via `?? defaultOn`) is paired
    // with the explicit set; only ids in the explicit set survive a
    // flush to the server. See ADR-0082 (supersedes ADR-0040's
    // persistence shape).
    layerPrefs: state.layerPrefs,
    layerPrefsExplicit: state.layerPrefsExplicit,
    reviewLayerPrefs: state.reviewLayerPrefs,
    reviewLayerPrefsExplicit: state.reviewLayerPrefsExplicit,
    // User's favourite trip ids — synced via userPrefs but cached
    // locally so the trips list doesn't flash unsorted on cold start.
    favoriteTripIds: state.favoriteTripIds,
    // Synced via userPrefs; cached locally so the next launch
    // honors the user's haptics choice before sync resolves.
    hapticsEnabled: state.hapticsEnabled,
    // User-prefs sync bookkeeping. dirtyUserPrefs persists so a tab
    // close doesn't drop a pending pref edit; userPrefsHydratedAt
    // lets the next launch skip the hydrate when the server copy is
    // already current.
    dirtyUserPrefs: state.dirtyUserPrefs,
    userPrefsHydratedAt: state.userPrefsHydratedAt,
    // ADR-0059 phase 3c: persist Frankfurter rates so the
    // settlement view works offline. Past-date rates are
    // immutable so the cache never invalidates.
    fxRates: state.fxRates,
    // Per-trip day cursor — lets the next launch land the user on
    // the day they were last viewing. See ADR-0068.
    lastViewedDayIndex: state.lastViewedDayIndex,
    // User-customized hero height (px). Local-only UI pref; null falls
    // back to the responsive default until the user drag-resizes.
    heroHeight: state.heroHeight,
    // ADR-0091 — synced via userPrefs; cached locally so an anonymous
    // user's dismissal survives a cold start before sync resolves, and
    // so the walkthrough auto-start gate reads the right value on mount.
    tutorialDisabled: state.tutorialDisabled,
    tutorialSeenAt: state.tutorialSeenAt,
    // ADR-0091 — local-only onboarding marker; persisted so a force-quit
    // mid-tour re-shows the walkthrough on the next open of that trip.
    tutorialPendingTripId: state.tutorialPendingTripId,
  };
}

/** The exact shape Zustand persist writes — derived from `partialize`
 *  so the two stay in lockstep. `Required` strips the implicit
 *  optional-marker (`?`) from inferred keys but does NOT remove an
 *  explicit `| undefined` from a value type. The codebase convention
 *  is `T | null` for nullable state (never `T | undefined`), so the
 *  practical guarantee is: a `persistedDefaults()` that omits a key,
 *  or returns a wrong VALUE type for a key, fails to compile. A
 *  defaults entry of `undefined` against a non-`| undefined` slice
 *  field also fails (e.g. `trips: undefined` against `trips: Trip[]`).
 *  The convention break — declaring a slice field as `T | undefined`
 *  — would slip an undefined default through; reviewer-enforced. */
type Persisted = Required<ReturnType<typeof partializeItineraryState>>;

/**
 * Defaults for every persisted key. Function call (not a cached
 * object) so each rehydrate gets a fresh shape and a future
 * non-spread mutation in a slice action can't leak across resets.
 * Type-checked against `Persisted` so missing keys, wrong value
 * types, AND undefined values all fail to compile.
 */
function persistedDefaults(): Persisted {
  return {
    trips: [],
    activeTripId: null,
    notesMap: {},
    notesMetaMap: {},
    tagsMap: {},
    dirtyTrips: {},
    pendingDeletes: [],
    layerPrefs: defaultLayerPrefs(),
    layerPrefsExplicit: [],
    reviewLayerPrefs: defaultReviewLayerPrefs(),
    reviewLayerPrefsExplicit: [],
    favoriteTripIds: [],
    hapticsEnabled: DEFAULT_HAPTICS_ENABLED,
    dirtyUserPrefs: false,
    userPrefsHydratedAt: null,
    fxRates: {},
    lastViewedDayIndex: {},
    heroHeight: null,
    tutorialDisabled: false,
    tutorialSeenAt: null,
    tutorialPendingTripId: null,
  };
}

/** Collect all entity IDs within a trip — used for notes/tags scoping.
 *  Covers: trip, days, places, transports, customs, journeys, stays. */
export function getTripEntityIds(trip: Trip): string[] {
  const ids = [trip.id];
  for (const day of trip.days) {
    ids.push(day.id);
    for (const place of day.places) ids.push(place.id);
    for (const transport of day.transports ?? []) ids.push(transport.id);
    for (const custom of day.customs ?? []) ids.push(custom.id);
  }
  for (const stay of trip.stays ?? []) ids.push(stay.id);
  for (const journey of trip.journeys ?? []) ids.push(journey.id);
  for (const place of trip.wishlist ?? []) ids.push(place.id);
  return ids;
}

export const useItineraryStore = create<ItineraryState>()(
  devtools(
    persist(
      (set, get, store) => ({
        ...createTripSlice(set, get, store),
        ...createDaySlice(set, get, store),
        ...createPlaceSlice(set, get, store),
        ...createTransportSlice(set, get, store),
        ...createCustomSlice(set, get, store),
        ...createJourneySlice(set, get, store),
        ...createStaySlice(set, get, store),
        ...createWishlistSlice(set, get, store),
        ...createParticipantSlice(set, get, store),
        ...createCostItemsSlice(set, get, store),
        ...createFxSlice(set, get, store),
        ...createNotesSlice(set, get, store),
        ...createHistorySlice(set, get, store),
        ...createSyncSlice(set, get, store),
        ...createViewSlice(set, get, store),
        ...createLayersSlice(set, get, store),
        ...createUserPrefsSlice(set, get, store),
        ...createReferencesSlice(set, get, store),
        ...createAttachmentsSlice(set, get, store),
      }),
      {
        name: STORAGE_KEY,
        version: 14,
        // `skipHydration: true` — the persist middleware would otherwise
        // hydrate eagerly under whatever scope was active at module-load
        // time (which is always `'anonymous'` since auth hasn't resolved
        // yet). AuthContext explicitly calls `useItineraryStore.persist
        // .rehydrate()` after it knows the user, so the right scope is
        // read on first hydrate instead of forcing a second pass.
        skipHydration: true,
        // Wraps AsyncStorage with per-user namespacing + Web Crypto
        // encryption on web (sandbox / OS handles at-rest on native).
        // See `apps/mobile/src/lib/scoped-encrypted-storage.ts`.
        storage: createJSONStorage(() => scopedEncryptedStorage),
        migrate: (persisted: unknown, version: number) => {
          const state = (persisted ?? {}) as {
            trips?: Trip[] | undefined;
            lockedMap?: unknown;
            layerPrefs?: unknown;
            reviewLayerPrefs?: unknown;
            layerPrefsExplicit?: unknown;
            reviewLayerPrefsExplicit?: unknown;
          };
          // v11 (ADR-0055 cutover): Stay + JourneyAnchor moved from flat
          // {name,lat,lng,...} to {place: PlaceRef, customLabel}. Devices
          // that persisted trips before the cutover still have the legacy
          // shape; rendering them crashes on `stay.place.name`. Run the
          // shared on-read migrator over each trip — it's idempotent, so
          // already-migrated entries short-circuit.
          if (version < 11 && Array.isArray(state.trips)) {
            state.trips = state.trips.map(migrateTripPlaces);
          }
          // v12: drop persisted `lockedMap`. The card-lock affordance
          // was rendered only by the deleted MobilePlaceCard tree; the
          // live TripReviewView never wired it. Keeping the field would
          // be dead state. Reintroduce per-trip when a real lock UI
          // surface lands.
          if (version < 12 && state.lockedMap !== undefined) {
            delete state.lockedMap;
          }
          // v13 (ADR-0082 cutover): persistence shape of layerPrefs /
          // reviewLayerPrefs splits into an effective values map + an
          // explicit-toggle set. Per the ADR, we intentionally do NOT
          // attempt to recover the user's old toggles — the v12 stored
          // map carries no "explicit vs default" signal, so preserving
          // it as explicit would re-pin the old defaults (the exact bug
          // ADR-0082 fixes). Wipe both maps + seed empty explicit sets.
          // The custom merge in this store seeds `layerPrefs` /
          // `reviewLayerPrefs` from `defaultLayerPrefs()` /
          // `defaultReviewLayerPrefs()` automatically post-rehydrate.
          if (version < 13) {
            delete state.layerPrefs;
            delete state.reviewLayerPrefs;
            state.layerPrefsExplicit = [];
            state.reviewLayerPrefsExplicit = [];
          }
          // v14 (ADR-0091): added `tutorialDisabled` / `tutorialSeenAt` /
          // `tutorialPendingTripId`. All additive and absent from pre-v14
          // blobs — the `merge` step below seeds them from
          // `persistedDefaults()` (false / null), so no transform is
          // needed here. The version bump exists to make the additive
          // change explicit in the migration ledger.
          return state;
        },
        partialize: partializeItineraryState,
        // Custom merge: reset every persisted key to its default before
        // applying the stored values. Without this step Zustand's default
        // merge (`{...current, ...persisted}`) leaks previous-scope keys
        // that are absent from the target scope's storage — the exact bug
        // that motivated `resetAndRehydrate`'s now-removed destructive
        // setState. Also resets transient scope-bound state (undo history,
        // active-trip role) so the next user can't `Cmd+Z` into the
        // previous user's data.
        merge: (persistedState, currentState) => ({
          ...currentState,
          ...persistedDefaults(),
          activeTripRole: null,
          _past: [],
          ...(persistedState as object),
        }) as ItineraryState,
        onRehydrateStorage: () => () => {
          // eslint-disable-next-line no-console -- sync diagnostics
          console.info('[sync] persist rehydrated');
          _storeHydrated = true;
          for (const l of _storeHydrationListeners.splice(0)) l();
        },
      }
    ),
    { name: 'ItineraryStore' }
  )
);
